import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import apiService from '../services/apiService';
import logger from '../utils/logger';
import * as appStorage from '../utils/appIndexedDB';

const AUTH_STORAGE_KEY = 'mygenie_auth';
const TOKEN_STORAGE_KEY = 'mygenie_token';
const REFRESH_TOKEN_STORAGE_KEY = 'mygenie_refresh_token';

// 將目前使用者身分（不含 token）同步給 Sentry，讓錯誤報告能標示是哪個使用者觸發的
const syncSentryUser = (user) => {
  Sentry.setUser(user ? { id: user.empno || user.username, username: user.username } : null);
};

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMplusLoggingIn, setIsMplusLoggingIn] = useState(false);
  // 本地 token 驗證/換新皆失敗而被清除登入狀態時設為 true，用於區分「登入逾時」與「尚未登入」畫面
  const [sessionExpired, setSessionExpired] = useState(false);
  // 本次開站是否剛執行過一次明確登入動作（login/ssoLogin/mplusLogin 任一成功)；
  // 開站時被動驗證/換新既有 token 不算登入動作、不設此旗標。
  // 用 ref 而非 state：只需被讀取一次決定是否還原上次對話，不需要觸發重新渲染。
  const justLoggedInRef = useRef(false);

  // 初始化時檢查本地儲存的登入狀態
  useEffect(() => {
    const checkAuthStatus = async () => {
      logger.auth('開始檢查登入狀態');
      
      try {
        const storedToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
        const storedAuth = await appStorage.getItem(AUTH_STORAGE_KEY);
        const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        
        logger.auth('檢查本地儲存', {
          hasToken: !!storedToken,
          hasAuth: !!storedAuth,
          tokenLength: storedToken ? storedToken.length : 0
        });
        
        if (storedToken && storedAuth) {
          const authData = JSON.parse(storedAuth);
          
          if (authData.user) {
            // 登入狀態的有效期一律由後端 token 決定（access token 60 分鐘、refresh token 90 天，
            // 且剩餘 30 天內會自動續期）。此處刻意不再另設本地效期上限：
            // 初版曾有「本地登入超過 7 天就清除」的保險絲，那是尚未導入 refresh 機制時的產物，
            // 而 refresh 並不會更新 authData.timestamp，等於讓使用者從首次登入起算滿 7 天就必被登出，
            // 與 refresh token 的效期設計直接衝突。
            if (authData.timestamp) {
              logger.auth('檢查本地登入時間', {
                loginTime: new Date(authData.timestamp).toISOString(),
                age: `${Math.floor((Date.now() - authData.timestamp) / (1000 * 60 * 60))} 小時`
              });
            }

            try {
              // 開啟網站時先嘗試換新 token
              let tokenValid = false;
              if (refreshToken) {
                logger.auth('開啟網站，嘗試換新 Token');
                const refreshResponse = await apiService.refreshToken(refreshToken);
                if (refreshResponse.success) {
                  logger.auth('Token 換新成功，恢復使用者狀態', {
                    username: authData.user.username
                  });
                  tokenValid = true;
                } else {
                  logger.auth('Token 換新失敗，fallback 至驗證現有 Token');
                }
              }

              // 換新失敗或無 refresh token，改驗證現有 token
              if (!tokenValid) {
                logger.auth('開始驗證現有 Token 有效性');
                const response = await apiService.verifyToken(storedToken);
                if (response.success) {
                  logger.auth('Token 驗證成功，恢復使用者狀態', {
                    username: authData.user.username
                  });
                  tokenValid = true;
                }
              }

              if (tokenValid) {
                setUser(authData.user);
                syncSentryUser(authData.user);
              } else {
                logger.auth('Token 無效，清除本地儲存');
                await clearAuthStorage();
                setSessionExpired(true);
              }
            } catch (error) {
              logger.error('AUTH', 'Token 換新／驗證 API 呼叫失敗', {
                error: error.message,
                fallback: '保留本地登入狀態'
              });
              // API 呼叫失敗（網路問題），保留本地登入狀態
              setUser(authData.user);
              syncSentryUser(authData.user);
            }
          }
        } else {
          logger.auth('沒有找到有效的本地登入資訊');
        }
      } catch (error) {
        logger.error('AUTH', '檢查登入狀態時發生錯誤', {
          error: error.message
        });
        await clearAuthStorage();
      } finally {
        logger.auth('登入狀態檢查完成');
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // 登入成功後請求瀏覽器通知權限（僅在支援且尚未決定時）
  const requestNotificationPermission = () => {
    if (typeof window === 'undefined' || !window.Notification) return;
    if (Notification.permission !== 'default') return;
    Notification.requestPermission().then((permission) => {
      logger.auth('通知權限結果', { permission });
    }).catch((err) => {
      logger.warn('AUTH', '請求通知權限失敗', { error: err?.message });
    });
  };

  // 清除認證儲存的輔助函數
  const clearAuthStorage = async () => {
    logger.auth('清除本地認證儲存');
    await Promise.all([
      appStorage.removeItem(AUTH_STORAGE_KEY),
      appStorage.removeItem(TOKEN_STORAGE_KEY),
      appStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY),
    ]);
  };

  // 登入
  const login = async (credentials) => {
    logger.auth('開始登入流程', {
      username: credentials.username,
      hasPassword: !!credentials.password
    });
    
    try {
      // 呼叫後端登入 API
      logger.auth('嘗試後端 API 登入');
      const response = await apiService.login(credentials);
      
      if (response.success && response.data) {
        const { user, token, refreshToken } = response.data;
        
        logger.auth('後端登入成功', {
          username: user.username,
          tokenLength: token.length,
          hasRefreshToken: !!refreshToken,
          authSource: user.authSource
        });
        
        // 儲存 token 和使用者資訊
        await appStorage.setItem(TOKEN_STORAGE_KEY, token);
        await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
        
        
        const authData = {
          user: {
            ...user,
            loginTime: new Date().toISOString(),
          },
          timestamp: Date.now(),
        };
        
        await appStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
        setUser(authData.user);
        justLoggedInRef.current = true;
        syncSentryUser(authData.user);
        setSessionExpired(false);
        requestNotificationPermission();

        logger.success('AUTH', '登入完成', {
          username: user.username,
          loginTime: authData.user.loginTime
        });
        
        return { success: true, message: response.message || '登入成功' };
      } else {
        logger.warn('AUTH', '後端登入失敗', {
          message: response.message
        });
        return { success: false, message: response.message || '登入失敗' };
      }
    } catch (error) {
      logger.error('AUTH', '後端登入 API 呼叫失敗', {
        error: error.message
      });
      
      return { success: false, message: error.message || '登入失敗，請稍後再試' };
    }
  };

  // SSO 登入
  const ssoLogin = async (ssoToken) => {
    logger.auth('開始 SSO 登入流程', {
      hasToken: !!ssoToken
    });
    
    try {
      // 呼叫 SSO 登入 API
      logger.auth('嘗試 SSO 登入 API');
      const response = await apiService.ssoLogin(ssoToken);
      
      if (response.success && response.data) {
        const { user, token, refreshToken } = response.data;
        
        logger.auth('SSO 登入成功', {
          username: user.username,
          tokenLength: token.length,
          hasRefreshToken: !!refreshToken,
          authSource: user.authSource
        });
        
        // 儲存 token 和使用者資訊
        await appStorage.setItem(TOKEN_STORAGE_KEY, token);
        await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
        
        
        const authData = {
          user: {
            ...user,
            loginTime: new Date().toISOString(),
          },
          timestamp: Date.now(),
        };
        
        await appStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
        setUser(authData.user);
        justLoggedInRef.current = true;
        syncSentryUser(authData.user);
        setSessionExpired(false);
        requestNotificationPermission();

        logger.success('AUTH', 'SSO 登入完成', {
          username: user.username,
          loginTime: authData.user.loginTime
        });
        
        return { success: true, message: response.message || 'SSO 登入成功' };
      } else {
        logger.warn('AUTH', 'SSO 登入失敗', {
          message: response.message
        });
        return { success: false, message: response.message || 'SSO 登入失敗' };
      }
    } catch (error) {
      logger.error('AUTH', 'SSO 登入 API 呼叫失敗', {
        error: error.message
      });
      return { success: false, message: error.message || 'SSO 登入失敗，請稍後再試' };
    }
  };

  // M+ 登入
  const mplusLogin = async (auserid, token, apiEndpoint = '/mplus/login') => {
    logger.auth('開始 M+ 登入流程', {
      auserid,
      hasToken: !!token,
      apiEndpoint
    });
    
    // 設置 M+ 登入中狀態
    setIsMplusLoggingIn(true);
    
    try {
      // 呼叫 M+ 登入 API
      logger.auth('嘗試 M+ 登入 API', { apiEndpoint });
      const response = await apiService.mplusLogin(auserid, token, apiEndpoint);
      
      if (response.success && response.data) {
        const { user, token: responseToken, refreshToken } = response.data;
        
        logger.auth('M+ 登入成功', {
          username: user.username,
          tokenLength: responseToken ? responseToken.length : 0,
          hasRefreshToken: !!refreshToken,
          authSource: user.authSource
        });
        
        // 儲存 token 和使用者資訊（與 AD 登入處理方式一致）
        await appStorage.setItem(TOKEN_STORAGE_KEY, responseToken);
        await appStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
        
        const authData = {
          user: {
            ...user,
            loginTime: new Date().toISOString(),
          },
          timestamp: Date.now(),
        };
        
        await appStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
        setUser(authData.user);
        justLoggedInRef.current = true;
        syncSentryUser(authData.user);
        setSessionExpired(false);
        requestNotificationPermission();

        logger.success('AUTH', 'M+ 登入完成', {
          username: user.username,
          loginTime: authData.user.loginTime
        });
        
        return { success: true, message: response.message || 'M+ 登入成功' };
      } else {
        logger.warn('AUTH', 'M+ 登入失敗', {
          message: response.message
        });
        return { success: false, message: response.message || 'M+ 登入失敗' };
      }
    } catch (error) {
      logger.error('AUTH', 'M+ 登入 API 呼叫失敗', {
        error: error.message
      });
      return { success: false, message: error.message || 'M+ 登入失敗，請稍後再試' };
    } finally {
      // 無論成功或失敗，都清除 M+ 登入中狀態
      setIsMplusLoggingIn(false);
    }
  };

  // 登出
  const logout = async () => {
    logger.auth('開始登出流程');
    
    try {
      const token = await appStorage.getItem(TOKEN_STORAGE_KEY);
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      
      // 如果有 token 和 refreshToken，嘗試呼叫後端登出 API
      if (token && refreshToken) {
        logger.auth('嘗試後端 API 登出', {
          hasToken: !!token,
          hasRefreshToken: !!refreshToken
        });
        try {
          await apiService.logout(token, refreshToken);
          logger.success('AUTH', '後端登出成功');
        } catch (error) {
          logger.error('AUTH', '登出 API 呼叫失敗', {
            error: error.message,
            fallback: '繼續清除本地狀態'
          });
          // 即使 API 呼叫失敗，也要清除本地狀態
        }
      } else {
        logger.auth('沒有找到 token 或 refreshToken，直接清除本地狀態');
      }
      
      // 清除本地儲存
      await clearAuthStorage();
      setUser(null);
      syncSentryUser(null);
      setSessionExpired(false);

      logger.success('AUTH', '登出完成');
      return { success: true, message: '登出成功' };
    } catch (error) {
      logger.error('AUTH', '登出過程發生錯誤', {
        error: error.message
      });
      return { success: false, message: '登出失敗' };
    }
  };

  // 檢查是否已登入（useCallback 避免每次 render 新函式參考，造成依賴它的 useEffect 重複執行）
  const isAuthenticated = useCallback(() => {
    return user !== null;
  }, [user]);

  // 取得使用者資訊
  const getUserInfo = () => {
    return user;
  };

  // 讀取「本次是否剛執行過登入動作」並立即歸零，避免旗標殘留影響之後的判斷
  const consumeJustLoggedIn = () => {
    const value = justLoggedInRef.current;
    justLoggedInRef.current = false;
    return value;
  };

  // 取得 token
  const getToken = () => {
    return appStorage.getItemSync(TOKEN_STORAGE_KEY);
  };

  // 重新整理使用者資訊
  const refreshUserInfo = async () => {
    const token = getToken();
    if (token) {
      try {
        const response = await apiService.getUserInfo(token);
        if (response.success && response.data) {
          const updatedUser = {
            ...user,
            ...response.data,
          };
          
          const authData = {
            user: updatedUser,
            timestamp: Date.now(),
          };
          
          await appStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
          setUser(updatedUser);
          syncSentryUser(updatedUser);

          return { success: true, user: updatedUser };
        }
      } catch (error) {
        console.error('重新整理使用者資訊失敗:', error);
        return { success: false, error: error.message };
      }
    }
    return { success: false, error: '沒有有效的 token' };
  };

  return {
    user,
    isLoading,
    isMplusLoggingIn,
    sessionExpired,
    login,
    ssoLogin,
    mplusLogin,
    logout,
    isAuthenticated,
    getUserInfo,
    getToken,
    refreshUserInfo,
    consumeJustLoggedIn,
  };
};

export default useAuth;
